import { C } from '../../../lib/theme';
import { styles } from '../../../lib/styles';
import { addCalendarDays, fmtDate, fmtDateTime, NOTICE_WINDOW_CALENDAR_DAYS } from '../../../lib/util';
import type { Case } from '../../../domain/initial-cases';
import { StatusBadge } from '../../../components/StatusBadge';
import { DeadlineIndicator } from '../../../components/DeadlineIndicator';
import { useAppData } from '../../../state/contexts/AppDataContext';
import { useUIState } from '../../../state/contexts/UIStateContext';
import { isNotifiableDecision, notificationContact, notificationContactLabel, undeliveredNoticeReason } from '../../../state/actions/lifecycle';

export function NotificationsTab({ c }: { c: Case }) {
  const { now } = useAppData();
  const { setModal } = useUIState();
  // #543 — read-only, and the only place an analyst can find this out. A case
  // recorded with the removed "Send via API" button (or by any caller the mail
  // producer refused) shows a ✓ and a method here and reads as notified; the
  // deadline and cure clocks running off it are real, and the notice is not.
  const undelivered = undeliveredNoticeReason(c);
  return (
    <div style={styles.card}>
      <h3 style={{ fontSize: 14, fontWeight: 600, marginBottom: 16 }}>Notification History</h3>
      {c.notificationSentAt ? (
        <div>
          <div style={{ color: C.success }}>✓ Notification sent: {fmtDateTime(c.notificationSentAt)}</div>
          {/* #693 — the resolved contact, not the retired OA columns. This
              line describes a notice ALREADY SENT, so it is a best reading of
              who it reached rather than a record of it: nothing stores the
              address a past notice was addressed to except the EMAIL_SENT audit
              row, which the Audit tab shows. Said plainly here rather than
              implied, because an analyst chasing "the registrant says nothing arrived"
              needs to know which of the two they are looking at. */}
          <div style={{ fontSize: 12, color: C.textMuted }}>
            Method: {c.notificationMethod} · To: {notificationContactLabel(c)}
            {notificationContact(c) ? ` (${notificationContact(c)!.email})` : ' (no contact on the submission)'}
          </div>
          <div style={{ fontSize: 12, color: C.textMuted }}>30-day deadline: {fmtDate(c.notificationDeadline)}</div>
          {undelivered && (
            <div style={{ background: C.dangerLight, borderRadius: 12, padding: 12, marginTop: 12, border: `2px solid ${C.danger}` }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.danger, marginBottom: 4 }}>Recorded, not delivered</div>
              <div style={{ fontSize: 12 }}>{undelivered}</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 8 }}>
                The deadlines above — and any cure window below — were started by that record and still
                stand. Treat the registrant as un-notified and follow up outside the portal.
              </div>
            </div>
          )}
        </div>
      ) : isNotifiableDecision(c.decision) ? (
        <div>
          <div style={{ color: C.warn }}>⚠ Notification pending</div>
          <DeadlineIndicator deadline={addCalendarDays(c.decisionDate!, NOTICE_WINDOW_CALENDAR_DAYS).toISOString()} label="30-day deadline" now={now} />
          <button style={{ ...styles.btn, ...styles.btnGold, marginTop: 12 }} onClick={() => setModal('send-notification')}>Send Notification</button>
        </div>
      ) : <div style={{ color: C.textMuted }}>No decision yet — notification not required</div>}
      {c.cureDeadline && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Cure Window</h4>
          <div>Status: <StatusBadge status={c.cureStatus!} /></div>
          <DeadlineIndicator deadline={c.cureDeadline} label="12 biz day deadline" now={now} />
        </div>
      )}
      {c.appealReceivedAt && (
        <div style={{ marginTop: 20, paddingTop: 16, borderTop: `1px solid ${C.border}` }}>
          <h4 style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Appeal</h4>
          <div>Received: {fmtDateTime(c.appealReceivedAt)}</div>
          <DeadlineIndicator deadline={c.appealDeadline} label="90-day reassessment" now={now} />
          {c.finalDetermination && <div style={{ marginTop: 8 }}>Final Determination: <StatusBadge status={c.finalDetermination} /> ({fmtDate(c.finalDeterminationDate)})</div>}
        </div>
      )}
    </div>
  );
}
